import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import type { AuditCategory, AuditEvent, AuditEventList } from "../../../api/types";
import { useSession } from "../../../auth/AuthProvider";
import { homeFor } from "../../../auth/pages";
import { CursorListView } from "../../../components/CursorListView";
import { Banner, Card, EmptyState, PageHeader, Select, StatusPill, Table, type Tone } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useCursorList } from "../../../lib/useCursorList";
import { AUDIT_CATEGORY_LABEL, AUDIT_TIER_TEXT, auditTier, humanize, OrgTabs } from "./orgCopy";

// Organization → Audit log (G-07; UX-DESIGN.md §4.5). GET /v1/audit-events
// ?category=&actor=&cursor= (org.go:568-662), newest first. What a member
// sees is decided by the RLS policy (0062_organization.up.sql:399-411),
// not by this screen: owner/admin everything, spend/payout approvers the
// financial and dispute categories plus their own events, anyone else
// only events they performed or that are about them. Details are
// redacted when written (_audit_redact, 0062:262-289).

const CATEGORIES = Object.keys(AUDIT_CATEGORY_LABEL) as AuditCategory[];

const OUTCOME: Record<AuditEvent["outcome"], { label: string; tone: Tone }> = {
  success: { label: "Succeeded", tone: "success" },
  failure: { label: "Failed", tone: "error" },
  pending: { label: "Pending", tone: "warning" },
  canceled: { label: "Canceled", tone: "neutral" },
};

function actorText(e: AuditEvent): string {
  if (e.actor.kind === "system") return "VADS (automatic)";
  if (e.actor.kind === "platform_staff") return "VADS staff";
  return e.actor.email ?? "A former member";
}

export function AuditLogPage() {
  const ep = useEndpoints();
  const session = useSession();
  const home = homeFor(session.partyType);
  const tier = auditTier(session.me.roles);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const actor = params.get("actor") ?? "";
  // Actor choices: the member list any member can read (org.go:44).
  const members = useQuery({ queryKey: ["org", "members"], queryFn: () => ep.org.listMembers() });
  const list = useCursorList<AuditEventList, AuditEvent>(
    ["audit", category, actor],
    (cursor) => ep.audit.list({ category: category || null, actor: actor || null, cursor }),
    (p) => p.events,
  );

  function setFilter(name: "category" | "actor", value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  return (
    <div className="stack-lg">
      <PageHeader title="Audit log" description="Security and financial actions on this organization's account: who did what, when, and the outcome." />
      <OrgTabs home={home} />
      <Banner tone="info" title="What you can see depends on your role">
        {AUDIT_TIER_TEXT[tier]}
      </Banner>
      <Card title="Events" subtitle="Newest first. Secrets, key material, raw IP addresses and payout-destination details are never recorded.">
        <div className="stack">
          <div className="grid-2">
            <Select
              label="Category"
              value={category}
              onChange={(e) => setFilter("category", e.target.value)}
              options={[{ value: "", label: "All categories" }, ...CATEGORIES.map((c) => ({ value: c, label: AUDIT_CATEGORY_LABEL[c] }))]}
            />
            <Select
              label="Member"
              value={actor}
              onChange={(e) => setFilter("actor", e.target.value)}
              options={[{ value: "", label: "Anyone" }, ...(members.data?.members ?? []).map((m) => ({ value: m.user_id, label: m.email }))]}
              hint="Events this member performed."
            />
          </div>
          <CursorListView
            list={list}
            loadingLabel="Loading audit events"
            empty={
              <EmptyState icon="list" title={category || actor ? "No events match these filters" : "No events yet"}>
                {tier === "full" ? "Nothing has been recorded yet." : "Only the events your role can see are listed."}
              </EmptyState>
            }
          >
            {(rows) => (
              <Table
                caption="Audit events"
                captionHidden
                rows={rows}
                rowKey={(e) => e.id}
                columns={[
                  { key: "at", header: "When", cell: (e) => formatLocal(e.at) },
                  { key: "actor", header: "Actor", cell: (e) => <span className="break-all">{actorText(e)}</span> },
                  { key: "cat", header: "Category", cell: (e) => AUDIT_CATEGORY_LABEL[e.category] ?? e.category },
                  { key: "action", header: "Action", cell: (e) => humanize(e.action) },
                  {
                    key: "resource",
                    header: "Affected",
                    cell: (e) => (
                      <span className="small">
                        {humanize(e.resource.type)}
                        {e.resource.id ? (
                          <>
                            {" "}
                            <code className="xs break-all">{e.resource.id}</code>
                          </>
                        ) : null}
                      </span>
                    ),
                  },
                  { key: "outcome", header: "Outcome", cell: (e) => <StatusPill tone={OUTCOME[e.outcome].tone}>{OUTCOME[e.outcome].label}</StatusPill> },
                  { key: "where", header: "Network / device", cell: (e) => e.location_hint ?? <span className="muted">—</span> },
                ]}
              />
            )}
          </CursorListView>
        </div>
      </Card>
    </div>
  );
}
